import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { OvercutStockDialog } from "@/components/OvercutStockDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ShipmentSignatureDialog from "@/components/ShipmentSignatureDialog";
import CuttingRecordDialog from "@/components/CuttingRecordDialog";
import QualityControlDialog from "@/components/QualityControlDialog";
import {
  Loader2,
  Play,
  CheckCircle2,
  ArrowRight,
  MoreVertical,
  User,
  Calendar,
  AlertTriangle,
  Trash2,
  PenLine,
  Package,
  Scissors,
  Printer,
  Sparkles,
  Shirt,
  ShieldCheck,
  PackageCheck,
  Truck as TruckIcon,
} from "lucide-react";
import { toast } from "sonner";

const DEPT_ICONS = {
  kesim: Scissors,
  baski: Printer,
  nakis: Sparkles,
  dikim: Shirt,
  kalite: ShieldCheck,
  paketleme: PackageCheck,
  sevkiyat: TruckIcon,
};

const STATUS_META = {
  waiting: { label: "Bekleyen", color: "text-slate-300", bg: "bg-slate-800/60", border: "border-slate-700" },
  in_progress: { label: "Devam Eden", color: "text-blue-300", bg: "bg-blue-950/40", border: "border-blue-900" },
  completed: { label: "Tamamlanan", color: "text-emerald-300", bg: "bg-emerald-950/40", border: "border-emerald-900" },
  delayed: { label: "Gecikmiş", color: "text-red-300", bg: "bg-red-950/40", border: "border-red-900" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }); } catch { return "—"; }
};

export default function Departments() {
  const [meta, setMeta] = useState({ departments: [], labels: {} });
  const [summary, setSummary] = useState([]);
  const [activeDept, setActiveDept] = useState("kesim");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [editForm, setEditForm] = useState({ assigned_employee: "", planned_end: "", completed_quantity: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigOrder, setSigOrder] = useState(null);
  const [employees, setEmployees] = useState([]);
  // Kesim (fazla kesim yönetimi) dialog state — paylaşılan CuttingRecordDialog
  const [cutOpen, setCutOpen] = useState(false);
  const [cutJob, setCutJob] = useState(null);
  // Kalite Kontrol dialog state — paylaşılan QualityControlDialog
  const [qcOpen, setQcOpen] = useState(false);
  const [qcJob, setQcJob] = useState(null);

  const loadMeta = async () => {
    const m = await api.get("/departments/meta");
    setMeta(m.data);
  };

  const loadSummary = async () => {
    const s = await api.get("/departments/summary");
    setSummary(s.data.rows);
  };

  const loadBoard = async (dept) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/departments/${dept}/jobs`);
      setBoard(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadSummary();
  }, []);

  useEffect(() => {
    loadBoard(activeDept);
    // Load employees that belong to this department (or unassigned) so the picker is scoped
    (async () => {
      try {
        const { data } = await api.get("/employees", { params: { department: activeDept, status: "aktif" } });
        setEmployees(data);
      } catch (_) {}
    })();
  }, [activeDept]);

  const refreshAll = async () => {
    await Promise.all([loadBoard(activeDept), loadSummary()]);
  };

  const startJob = async (job) => {
    try {
      await api.post(`/departments/jobs/${job.id}/start`);
      toast.success(`${job.order_no} başlatıldı`);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeJob = async (job) => {
    try {
      await api.post(`/departments/jobs/${job.id}/complete`);
      toast.success(`${job.order_no} tamamlandı`);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const moveNext = async (job) => {
    try {
      const { data } = await api.post(`/departments/jobs/${job.id}/move-next`);
      const nextDept = data?.next_job?.department;
      toast.success(`${job.order_no} → ${meta.labels[nextDept] || nextDept}`);
      refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`${job.order_no} bu departmandan silinecek. Emin misiniz?`)) return;
    try {
      await api.delete(`/departments/jobs/${job.id}`);
      toast.success("Silindi");
      refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (job) => {
    setEditJob(job);
    setEditForm({
      assigned_employee: job.assigned_employee || "",
      assigned_employee_id: job.assigned_employee_id || "",
      planned_end: job.planned_end ? job.planned_end.slice(0, 10) : "",
      completed_quantity: job.completed_quantity || 0,
      notes: job.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        assigned_employee: editForm.assigned_employee,
        assigned_employee_id: editForm.assigned_employee_id || "",
        planned_end: editForm.planned_end ? new Date(editForm.planned_end).toISOString() : null,
        completed_quantity: Number(editForm.completed_quantity),
        notes: editForm.notes,
      };
      await api.patch(`/departments/jobs/${editJob.id}`, payload);
      toast.success("Güncellendi");
      setEditOpen(false);
      refreshAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const openSignature = (job) => {
    setSigOrder({ id: job.order_id, order_no: job.order_no, customer_name: job.customer_name });
    setSigOpen(true);
  };

  const openCutting = (job) => {
    setCutJob(job);
    setCutOpen(true);
  };

  const openQuality = (job) => {
    setQcJob(job);
    setQcOpen(true);
  };

  const [overcutJob, setOvercutJob] = useState(null);
  const addOvercutToStock = (job) => setOvercutJob(job);

  const buckets = useMemo(() => {
    if (!board) return { waiting: [], in_progress: [], completed: [], delayed: [] };
    return board.buckets;
  }, [board]);

  const summaryFor = (dept) => summary.find((r) => r.department === dept);

  return (
    <div>
      <PageHeader
        title="Üretim Departmanları"
        subtitle="Kesim → Sevkiyat · Departman bazlı iş takibi"
        breadcrumbs={["Modüller", "Departmanlar"]}
      />

      <div className="p-4 lg:p-6">
        <Tabs value={activeDept} onValueChange={setActiveDept}>
          <TabsList className="bg-slate-900 border border-slate-800 rounded-sm h-auto p-1 mb-5 flex-wrap justify-start w-full overflow-x-auto">
            {meta.departments.map((d) => {
              const Icon = DEPT_ICONS[d] || Package;
              const s = summaryFor(d);
              const total = s ? s.waiting + s.in_progress : 0;
              const delayed = s?.delayed || 0;
              return (
                <TabsTrigger
                  key={d}
                  value={d}
                  data-testid={`dept-tab-${d}`}
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-3 py-2 flex items-center gap-1.5"
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{meta.labels[d]}</span>
                  <span className="ml-1 font-mono tabular-nums text-[10px] opacity-80">{total}</span>
                  {delayed > 0 && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-red-400 font-mono text-[10px]">
                      <AlertTriangle className="w-2.5 h-2.5" />{delayed}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {meta.departments.map((d) => (
            <TabsContent key={d} value={d} className="mt-0">
              {loading || !board ? (
                <div className="flex items-center justify-center py-24">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {["waiting", "in_progress", "delayed", "completed"].map((status) => {
                    const meta = STATUS_META[status];
                    const items = status === "delayed"
                      ? buckets.delayed
                      : (buckets[status] || []).filter((j) => !(status !== "completed" && j.is_delayed));
                    return (
                      <Card key={status} data-testid={`dept-column-${status}`} className={`bg-slate-900 ${meta.border} border rounded-sm overflow-hidden flex flex-col`}>
                        <div className={`px-3 py-2.5 border-b ${meta.border} ${meta.bg} flex items-center justify-between`}>
                          <div className={`text-xs font-semibold uppercase tracking-wider ${meta.color}`}>
                            {meta.label}
                          </div>
                          <div className="text-[10px] font-mono text-slate-400 tabular-nums">{items.length}</div>
                        </div>
                        <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto kanban-scroll min-h-24">
                          {items.length === 0 ? (
                            <div className="text-[10px] text-slate-600 font-mono uppercase tracking-widest text-center py-6">— Boş —</div>
                          ) : (
                            items.map((j) => <JobCard
                              key={j.id}
                              job={j}
                              onStart={() => startJob(j)}
                              onComplete={() => completeJob(j)}
                              onMoveNext={() => moveNext(j)}
                              onEdit={() => openEdit(j)}
                              onDelete={() => deleteJob(j)}
                              onSignature={activeDept === "sevkiyat" ? () => openSignature(j) : null}
                              onCutting={activeDept === "kesim" ? () => openCutting(j) : null}
                              onAddToStock={activeDept === "kesim" ? () => addOvercutToStock(j) : null}
                              onQuality={activeDept === "kalite" ? () => openQuality(j) : null}
                            />)
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">İş Detayı — {editJob?.order_no}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Atanan Çalışan</Label>
                <Select
                  value={editForm.assigned_employee_id || "none"}
                  onValueChange={(v) => {
                    if (v === "none") {
                      setEditForm({ ...editForm, assigned_employee_id: "", assigned_employee: "" });
                    } else {
                      const emp = employees.find((e) => e.id === v);
                      setEditForm({ ...editForm, assigned_employee_id: v, assigned_employee: emp?.name || "" });
                    }
                  }}
                >
                  <SelectTrigger data-testid="job-employee-select" className="bg-slate-950 border-slate-700 rounded-sm">
                    <SelectValue placeholder="Çalışan seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-64">
                    <SelectItem value="none">— Atanmamış —</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {employees.length === 0 && (
                  <div className="text-[10px] text-slate-500 font-mono">
                    Bu departmanda aktif çalışan yok. "Çalışanlar" modülünden ekleyin.
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Planlanan Bitiş</Label>
                <Input
                  type="date"
                  value={editForm.planned_end}
                  onChange={(e) => setEditForm({ ...editForm, planned_end: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Tamamlanan Adet ({editJob?.quantity || 0} hedef)</Label>
                <Input
                  data-testid="job-completed-input"
                  type="number"
                  min="0"
                  max={editJob?.quantity || 0}
                  value={editForm.completed_quantity}
                  onChange={(e) => setEditForm({ ...editForm, completed_quantity: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Üretim Notları</Label>
                <Textarea
                  rows={3}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
                İptal
              </Button>
              <Button data-testid="save-job-btn" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ShipmentSignatureDialog
        open={sigOpen}
        onOpenChange={setSigOpen}
        order={sigOrder}
        onSaved={refreshAll}
      />

      {/* Kesim Kaydı — paylaşılan dialog (fazla kesim backend REUSE) */}
      <CuttingRecordDialog
        order={cutJob ? {
          id: cutJob.order_id,
          order_no: cutJob.order_no,
          customer_name: cutJob.customer_name,
          product_name: cutJob.product_name,
        } : null}
        open={cutOpen}
        onOpenChange={setCutOpen}
        onSaved={refreshAll}
      />

      {/* Kalite Kontrol — paylaşılan dialog (quality backend REUSE) */}
      <QualityControlDialog
        order={qcJob ? {
          id: qcJob.order_id,
          order_no: qcJob.order_no,
          customer_name: qcJob.customer_name,
          product_name: qcJob.product_name,
        } : null}
        open={qcOpen}
        onOpenChange={setQcOpen}
        onSaved={refreshAll}
      />
      <OvercutStockDialog job={overcutJob} onClose={() => setOvercutJob(null)} onDone={refreshAll} />
    </div>
  );
}

function JobCard({ job, onStart, onComplete, onMoveNext, onEdit, onDelete, onSignature, onCutting, onAddToStock, onQuality }) {
  const delayed = job.is_delayed && job.status !== "completed";
  return (
    <Card
      data-testid={`job-card-${job.id}`}
      className={`bg-slate-950 border rounded-sm p-3 hover:border-slate-600 transition-colors ${
        delayed ? "border-red-900/70" : "border-slate-800"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-500">{job.order_no}</div>
          <div className="text-base font-bold text-white line-clamp-2 leading-tight mt-0.5" data-testid={`job-customer-${job.id}`}>
            {job.customer_name}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`job-menu-${job.id}`} className="text-slate-500 hover:text-slate-200 shrink-0 p-0.5">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-200 rounded-sm w-48">
            <DropdownMenuItem onClick={onEdit} className="text-xs cursor-pointer">
              <PenLine className="w-3 h-3 mr-2" /> Düzenle
            </DropdownMenuItem>
            {onSignature && (
              <DropdownMenuItem onClick={onSignature} className="text-xs cursor-pointer text-blue-400">
                <PenLine className="w-3 h-3 mr-2" /> İmza Al & Kapat
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onDelete} className="text-xs text-red-400 focus:text-red-300 cursor-pointer">
              <Trash2 className="w-3 h-3 mr-2" /> Sil
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="text-[10px] text-slate-500 line-clamp-2 mb-2" data-testid={`job-product-${job.id}`}>
        {job.product_name}
      </div>
      {job.job_type === "application" && job.application_name && (
        <div className="mb-2 inline-flex max-w-full rounded-sm border border-orange-900 bg-orange-950/30 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-orange-300" data-testid={`job-application-${job.id}`}>
          Uygulama: {job.application_name} · {job.quantity} {job.application_unit || "adet"}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap mb-2 text-[11px]">
        {job.assigned_employee && (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <User className="w-2.5 h-2.5" />
            <span className="truncate max-w-[100px]">{job.assigned_employee}</span>
          </span>
        )}
        <span className={`inline-flex items-center gap-1 font-mono ${delayed ? "text-red-400" : "text-slate-500"}`}>
          <Calendar className="w-2.5 h-2.5" />
          {fmtDate(job.planned_end)}
        </span>
      </div>

      {/* Progress */}
      <div className="space-y-1 mb-2">
        <div className="flex items-center justify-between text-[10px] font-mono">
          <span className="text-slate-500">{job.completed_quantity} / {job.quantity}</span>
          <span className={`tabular-nums ${job.progress_pct >= 100 ? "text-emerald-400" : "text-blue-500"}`}>
            {job.progress_pct}%
          </span>
        </div>
        <Progress value={job.progress_pct} className="h-1.5 bg-slate-800 [&>div]:bg-blue-500" />
      </div>

      {/* Kesim özeti — sadece kesim departmanı & cutting kaydı varsa */}
      {job.department === "kesim" && job.cutting && (
        <div data-testid={`cutting-summary-${job.id}`} className="mb-2 rounded-sm bg-slate-900/60 border border-slate-800 px-2 py-1.5 text-[10px] font-mono grid grid-cols-3 gap-1">
          <div><span className="text-slate-500">Plan:</span> <span className="text-slate-200 tabular-nums">{job.cutting.planned_qty}</span></div>
          <div><span className="text-slate-500">Kesilen:</span> <span className="text-slate-200 tabular-nums">{job.cutting.actual_qty}</span></div>
          <div className={job.cutting.overcut_qty > 0 ? "text-amber-400" : "text-slate-500"}>
            <span className="text-slate-500">Fazla:</span> <span className="tabular-nums">{job.cutting.overcut_qty > 0 ? `+${job.cutting.overcut_qty}` : "0"}</span>
          </div>
          {job.cutting.overcut_qty > 0 && (
            <div className="col-span-3 text-[9px] uppercase tracking-widest text-slate-500">
              {({continue: "Üretime Devam", holdback: "Yarı Mamul", waste: "Fire"})[job.cutting.disposition] || "—"}
              {job.cutting.extra_stock_added && <span className="ml-2 text-emerald-400">✓ Stoğa alındı</span>}
              {!job.cutting.extra_stock_added && job.cutting.extra_stock_qty > 0 && (
                <span className="ml-2 text-cyan-400" data-testid={`overcut-partial-${job.id}`}>Stokta {job.cutting.extra_stock_qty}/{job.cutting.overcut_qty}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Kalite özeti — sadece kalite departmanı & quality kaydı varsa */}
      {job.department === "kalite" && job.quality && (
        <div data-testid={`quality-summary-${job.id}`} className="mb-2 rounded-sm bg-slate-900/60 border border-slate-800 px-2 py-1.5 text-[10px] font-mono grid grid-cols-4 gap-1">
          <div><span className="text-slate-500">Üretilen:</span> <span className="text-slate-200 tabular-nums">{job.quality.produced_qty}</span></div>
          <div><span className="text-slate-500">Sağlam:</span> <span className="text-emerald-400 tabular-nums">{job.quality.passed_qty}</span></div>
          <div><span className="text-slate-500">Defolu:</span> <span className={job.quality.defective_qty > 0 ? "text-red-400 tabular-nums" : "text-slate-500 tabular-nums"}>{job.quality.defective_qty}</span></div>
          <div><span className="text-slate-500">Eksik:</span> <span className={job.quality.missing_qty > 0 ? "text-amber-400 tabular-nums" : "text-slate-500 tabular-nums"}>{job.quality.missing_qty}</span></div>
          {job.quality.applied && (
            <div className="col-span-4 text-[9px] uppercase tracking-widest text-emerald-400">✓ Siparişe uygulandı</div>
          )}
        </div>
      )}

      <div className="flex items-center gap-1.5 flex-wrap">
        {job.status === "waiting" && (
          <Button data-testid={`start-${job.id}`} size="sm" onClick={onStart} className="bg-blue-600 hover:bg-blue-500 rounded-sm text-[10px] uppercase tracking-wider font-semibold h-7 px-2">
            <Play className="w-2.5 h-2.5 mr-1" /> Başlat
          </Button>
        )}
        {job.status === "in_progress" && (
          <Button data-testid={`complete-${job.id}`} size="sm" onClick={onComplete} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm text-[10px] uppercase tracking-wider font-semibold h-7 px-2">
            <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> Bitir
          </Button>
        )}
        {job.status !== "completed" && (
          <Button data-testid={`move-next-${job.id}`} size="sm" variant="outline" onClick={onMoveNext} className="border-blue-900 bg-blue-950/30 text-blue-300 hover:bg-blue-950/60 hover:text-blue-200 rounded-sm text-[10px] uppercase tracking-wider font-semibold h-7 px-2">
            İleri <ArrowRight className="w-2.5 h-2.5 ml-1" />
          </Button>
        )}
        {job.department === "kesim" && onCutting && (
          <Button data-testid={`cutting-open-${job.id}`} size="sm" variant="outline" onClick={onCutting} className="border-amber-900 bg-amber-950/30 text-amber-300 hover:bg-amber-950/60 rounded-sm text-[10px] uppercase tracking-wider font-semibold h-7 px-2">
            <Scissors className="w-2.5 h-2.5 mr-1" /> Kesim
          </Button>
        )}
        {job.department === "kalite" && onQuality && (
          <Button data-testid={`quality-open-${job.id}`} size="sm" variant="outline" onClick={onQuality} className="border-emerald-900 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/60 rounded-sm text-[10px] uppercase tracking-wider font-semibold h-7 px-2">
            <ShieldCheck className="w-2.5 h-2.5 mr-1" /> Kalite
          </Button>
        )}
        {job.department === "kesim" && job.cutting?.overcut_qty > 0
          && ["continue","holdback"].includes(job.cutting?.disposition)
          && !job.cutting?.extra_stock_added && onAddToStock && (
          <Button data-testid={`overcut-to-stock-${job.id}`} size="sm" variant="outline" onClick={onAddToStock} className="border-emerald-900 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-950/60 rounded-sm text-[10px] uppercase tracking-wider font-semibold h-7 px-2">
            <Package className="w-2.5 h-2.5 mr-1" /> Stoğa Al (+{job.cutting.overcut_qty - (job.cutting.extra_stock_qty || 0)})
          </Button>
        )}
      </div>
    </Card>
  );
}
